import { useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DashboardProvider } from "../context/DashboardContext";
import { FreqRangeProvider } from "../context/FreqRangeContext";
import { PowerGammaProvider } from "../context/PowerGammaContext";
import { RibbonSettingsProvider } from "../context/RibbonSettingsContext";
import { useDashboardPresenceReport } from "../hooks/useDashboardPresenceReport";
import { canManageUsers, isAdmin } from "../lib/access";
import type { TopicId } from "../types/dashboard";
import { TOPICS } from "../registry/topics";
import { MainArea } from "./MainArea";
import { NavRail } from "./NavRail";
import { Sidebar } from "./Sidebar";
import { LiveDataPanel } from "./LiveDataPanel";
import { SettingsPanel } from "./SettingsPanel";
import { UsersPanel } from "./UsersPanel";

function pathToTopic(pathname: string): TopicId {
  const match = TOPICS.find((t) => t.path === pathname);
  return match?.id ?? "dashboard";
}

function ShellInner() {
  const location = useLocation();
  const { user } = useAuth();
  const topic = pathToTopic(location.pathname);
  useDashboardPresenceReport(topic, location.pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [liveDataOpen, setLiveDataOpen] = useState(false);
  const showAdmin = isAdmin(user);

  if ((topic === "admin" || topic === "devops") && !showAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="grid h-screen w-screen grid-cols-[var(--nav-rail-width)_var(--sidebar-width)_1fr] max-lg:grid-cols-[var(--nav-rail-width)_1fr]">
      <NavRail
        sidebarOpen={sidebarOpen}
        onMenuClick={() => setSidebarOpen((v) => !v)}
        usersOpen={usersOpen}
        onUsersClick={() => {
          if (!canManageUsers(user)) return;
          setUsersOpen((v) => !v);
          setSettingsOpen(false);
          setLiveDataOpen(false);
        }}
        settingsOpen={settingsOpen}
        onSettingsClick={() => {
          setSettingsOpen((v) => !v);
          setUsersOpen(false);
          setLiveDataOpen(false);
        }}
        liveDataOpen={liveDataOpen}
        onLiveDataClick={() => {
          setLiveDataOpen((v) => !v);
          setUsersOpen(false);
          setSettingsOpen(false);
        }}
        showAdmin={showAdmin}
      />

      <div
        className={[
          "max-lg:fixed max-lg:inset-y-0 max-lg:left-[var(--nav-rail-width)] max-lg:z-40 max-lg:shadow-lg",
          "max-lg:transition-transform",
          sidebarOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full",
          "lg:contents",
        ].join(" ")}
      >
        <Sidebar topic={topic} />
      </div>

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/20 lg:hidden"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <MainArea topicId={topic} />
      <UsersPanel open={usersOpen} onClose={() => setUsersOpen(false)} />
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <LiveDataPanel open={liveDataOpen} onClose={() => setLiveDataOpen(false)} />
    </div>
  );
}

export function DesktopShell() {
  return (
    <DashboardProvider>
      <PowerGammaProvider>
        <FreqRangeProvider>
            <RibbonSettingsProvider>
            <Routes>
              {TOPICS.map((t) => (
                <Route key={t.id} path={t.path} element={<ShellInner />} />
              ))}
              <Route path="/florian" element={<Navigate to="/ecg" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </RibbonSettingsProvider>
        </FreqRangeProvider>
      </PowerGammaProvider>
    </DashboardProvider>
  );
}
